import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 异步actions
export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (params, { rejectWithValue }) => {
    try {
      // 转换前端参数名到后端参数名
      const backendParams = {
        ...params,
        page: params.current || params.page || 1,
        limit: params.pageSize || params.limit || 20,
      };
      // 移除前端特有的参数（这些参数不应该发送到后端）
      delete backendParams.current;
      delete backendParams.pageSize;
      delete backendParams.total;

      const response = await api.get('/admin/users', { params: backendParams });

      return response.data; // 返回 data 字段中的内容
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取用户列表失败');
    }
  }
);

export const fetchUserDetail = createAsyncThunk(
  'user/fetchUserDetail',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/users/detail', { params: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取用户详情失败');
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/detail', data, { params: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新用户失败');
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'user/updateUserStatus',
  async ({ userId, status, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/status', { status, reason }, { params: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新用户状态失败');
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  'user/resetUserPassword',
  async ({ userId, newPassword }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/reset-password', {
        newPassword,
      }, { params: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '重置密码失败');
    }
  }
);

export const resetUserGa = createAsyncThunk(
  'user/resetUserGa',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/users/${userId}/reset-ga`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '重置Google验证器失败');
    }
  }
);

export const freezeUser = createAsyncThunk(
  'user/freezeUser',
  async ({ userId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/status', { status: 2, reason }, { params: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '冻结用户失败');
    }
  }
);

export const unfreezeUser = createAsyncThunk(
  'user/unfreezeUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/status', { status: 1, reason: '管理员解冻' }, { params: { userId } });
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '解冻用户失败';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchUserLevels = createAsyncThunk(
  'user/fetchUserLevels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/user-levels');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取用户等级失败');
    }
  }
);

export const createUserLevel = createAsyncThunk(
  'user/createUserLevel',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/user-levels', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '创建用户等级失败');
    }
  }
);

export const updateUserLevel = createAsyncThunk(
  'user/updateUserLevel',
  async ({ levelId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/user-levels/${levelId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新用户等级失败');
    }
  }
);

export const deleteUserLevel = createAsyncThunk(
  'user/deleteUserLevel',
  async (levelId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/admin/user-levels/${levelId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '删除用户等级失败');
    }
  }
);

export const createUser = createAsyncThunk(
  'user/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users', userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '创建用户失败');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/delete', {}, { params: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '删除用户失败');
    }
  }
);

export const batchDeleteUsers = createAsyncThunk(
  'user/batchDeleteUsers',
  async (userIds, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/users/batch', { userIds });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '批量删除用户失败');
    }
  }
);

export const adjustUserBalance = createAsyncThunk(
  'user/adjustUserBalance',
  async ({ userId, adjustmentType, amount, reason, payPassword }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/admin/users/balance-adjustment',
        { adjustmentType, amount, reason, payPassword },
        { params: { userId } }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '余额调整失败';
      return rejectWithValue(errorMessage);
    }
  }
);

export const merchantRecharge = createAsyncThunk(
  'user/merchantRecharge',
  async ({ userId, amount, remark }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/admin/users/merchant-recharge',
        { userId, amount, remark }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '充值失败';
      return rejectWithValue(errorMessage);
    }
  }
);

export const merchantWithdraw = createAsyncThunk(
  'user/merchantWithdraw',
  async ({ userId, amount, remark }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/admin/users/merchant-withdraw',
        { userId, amount, remark }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '提现失败';
      return rejectWithValue(errorMessage);
    }
  }
);

// 初始状态
const initialState = {
  users: [],
  userDetail: null,
  userLevels: [],
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {},
  isLoading: false,
  error: null,
};

// slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearUserDetail: (state) => {
      state.userDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取用户列表
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.list;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 获取用户详情
      .addCase(fetchUserDetail.fulfilled, (state, action) => {
        state.userDetail = action.payload;
      })
      // 更新用户
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.userDetail && state.userDetail.id === action.payload.id) {
          state.userDetail = action.payload;
        }
      })
      // 更新用户状态
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index].status = action.payload.status;
        }
      })
      // 冻结用户
      .addCase(freezeUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index].status = 'frozen';
        }
      })
      // 解冻用户
      .addCase(unfreezeUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index].status = 'active';
        }
      })
      // 获取用户等级
      .addCase(fetchUserLevels.fulfilled, (state, action) => {
        state.userLevels = action.payload;
      })
      // 创建用户等级
      .addCase(createUserLevel.fulfilled, (state, action) => {
        state.userLevels.push(action.payload);
      })
      // 更新用户等级
      .addCase(updateUserLevel.fulfilled, (state, action) => {
        const index = state.userLevels.findIndex(level => level.id === action.payload.id);
        if (index !== -1) {
          state.userLevels[index] = action.payload;
        }
      })
      // 删除用户等级
      .addCase(deleteUserLevel.fulfilled, (state, action) => {
        state.userLevels = state.userLevels.filter(level => level.id !== action.payload.id);
      })
      // 创建用户
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        // 将新用户添加到列表开头
        state.users.unshift(action.payload.user);
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 删除用户
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        // 从列表中移除已删除的用户
        state.users = state.users.filter(user => user.id !== action.payload.userId);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 批量删除用户
      .addCase(batchDeleteUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(batchDeleteUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        // 从列表中移除已删除的用户
        const deletedUserIds = action.payload.results.success.map(item => item.userId);
        state.users = state.users.filter(user => !deletedUserIds.includes(user.id));
        state.error = null;
      })
      .addCase(batchDeleteUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setFilters, setPagination, clearUserDetail } = userSlice.actions;
export default userSlice.reducer;
